import { PaymentMethod } from "@clarkinc/payment-client";
import React, { useEffect, useRef, useState } from "react";
import { cls } from "@wss/cls";
import { Modal } from "@clarkinc/zest-design-system/ui";
import { Throwable } from "@wss/error-tracking/throwable";
import { toError } from "@wss/error-tracking/utils";
import { logException } from "@wss/error-tracking";
import { ShippingAddress, SavedCardData, EnrollmentOption, PlusPotentialSavingsInfo } from "../Models";
import PaymentSelection from "./PaymentSelection";
import ShippingDropdown from "./ShippingDropdown";
import WebPlusPaymentDetails from "./WebPlusPaymentDetails";
import setWindowLocationHref from "~/common/navigation/setWindowLocationHref";
import { PaymentSelectionRef } from "./PaymentSelectionRefs";
import PinterestTagSignUpEvent from "~/analytics/TrackingPixels/TagEvents/PinterestTagSignUpEvent";
import PinterestApiEvent from "~/analytics/MarketingEvents/Pinterest/PinterestApiEvent";
import Errors from "./Errors";
import Footer from "./Footer";
import Header from "./Header";
import PlusSubscriptionModalStore, { ModalTypes } from "../PlusSubscriptionModalStore";
import "~/less/styles-ws-plus-modal.less";
import { plusSubscriptionProvider } from "../../../my-account/Plus/plusSubscriptionProvider";
type PlusSignupEndpointResponse = {
  wasCredited: boolean;
};
interface Props {
  userGuid: string | undefined;
  shippingAddresses: readonly ShippingAddress[];
  defaultShippingAddressId: number;
  formattedPlusPrice: string;
  savedCardData: SavedCardData[] | readonly SavedCardData[];
  enrollmentOptions: EnrollmentOption[] | readonly EnrollmentOption[];
  enrollment: EnrollmentOption | undefined;
  addressPlusSignup: boolean;
  plusPotentialSavingsInfo: PlusPotentialSavingsInfo | undefined;
  isFreeview?: boolean;
  subscriptionChargeDate?: Date;
  isOpenFromAd?: boolean;
}
const PlusSubscriptionModal = ({
  userGuid,
  shippingAddresses,
  defaultShippingAddressId,
  formattedPlusPrice,
  savedCardData,
  enrollmentOptions,
  enrollment,
  addressPlusSignup,
  plusPotentialSavingsInfo,
  isFreeview = false,
  subscriptionChargeDate,
  isOpenFromAd = false
}: Props) => {
  const plusModalId = "plus-subscription-modal";
  const plusSubscriptionModalOpen = PlusSubscriptionModalStore.useState(state => state.modalType === ModalTypes.PLUS);
  const [welcomeMessageIsVisible, setWelcomeMessageIsVisible] = useState(false);
  const [onCloseUrlUpdate, setOnCloseUrlUpdate] = useState<string | null>(null);
  const setPlusSubscriptionModalOpen = PlusSubscriptionModalStore.getState().setModalOpen;
  const onOpenChange = (open: boolean) => {
    setWelcomeMessageIsVisible(false);
    if (open) {
      setPlusSubscriptionModalOpen(ModalTypes.PLUS);
    } else {
      setPlusSubscriptionModalOpen(ModalTypes.CLOSED);
    }
  };
  const handleModalClose = () => {
    setCardVerificationError(false);
    setCardSaveError(false);
    setSubscribeError(false);
    setSubscribeCustomErrorMessage("");
    setMarketingOptIn(true);
    setShippingId(defaultShippingAddressId);
    setPlusSubscriptionModalOpen(ModalTypes.CLOSED);
    setWelcomeMessageIsVisible(false);
    if (onCloseUrlUpdate) {
      setWindowLocationHref(onCloseUrlUpdate);
    }
  };
  const todaysDate = new Date();
  const paymentSelectionRef = useRef<PaymentSelectionRef>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const [marketingOptIn, setMarketingOptIn] = useState<boolean>(true);
  const [promotionName, setPromotionName] = useState<string | undefined>(enrollment ? enrollment.promotionName : undefined);
  const [shippingId, setShippingId] = useState(defaultShippingAddressId);
  const defaultCard = savedCardData !== undefined ? savedCardData.filter(element => element.default)[0] ?? savedCardData[0] : undefined;
  const [savedCardId, setSavedCardId] = useState<number | undefined>(defaultCard?.num);
  const [cardSaveError, setCardSaveError] = useState(false);
  const [cardVerificationError, setCardVerificationError] = useState(false);
  const [subscribeError, setSubscribeError] = useState(false);
  const [subscribeCustomErrorMessage, setSubscribeCustomErrorMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const submitAddSubscriptionRequest = async (shippingId: number, tokenId = 0): Promise<Throwable> => {
    try {
      const result = await plusSubscriptionProvider().addSubscriptions(userGuid ?? "", [{
        shippingAddressId: shippingId,
        paymentTokenId: tokenId,
        promotionName: promotionName ?? null
      }]);
      if (!result.error) {
        handleShowWelcome();
        setOnCloseUrlUpdate(`${window.location.href.split("?")[0]}?enrolled=1`);
      } else {
        setSubscribeError(true);
        setSubmitting(false);
        setSubscribeCustomErrorMessage(result.error);
      }
      return {
        isError: false
      };
    } catch (e) {
      const error = new Error(`Error sending additional Plus signup`, {
        cause: toError(e)
      });
      setSubscribeError(true);
      setSubmitting(false);
      await logException(error);
      return {
        error,
        isError: true
      };
    }
  };
  const submitNewSubscriptionEnrollmentRequest = async (tokenId = 0): Promise<Throwable> => {
    const enrollRequest = new FormData();
    enrollRequest.append("savedCardId", tokenId.toString());
    enrollRequest.append("useTransactionsApi", "true");
    enrollRequest.append("shippingId", shippingId.toString());
    enrollRequest.append("promotionName", promotionName?.toString() ?? "");
    enrollRequest.append("marketingOptIn", marketingOptIn.toString());
    enrollRequest.append("accessor", "");
    if (plusPotentialSavingsInfo?.encryptedOrderNumber) {
      enrollRequest.append("plusPromotionEncryptedOrderNumber", plusPotentialSavingsInfo.encryptedOrderNumber);
    }
    try {
      const result = await fetch("/plus/enroll/", {
        method: "POST",
        body: enrollRequest
      });
      if (result.ok) {
        new PinterestApiEvent("signup", new PinterestTagSignUpEvent("Webplus")).sendEvent();
        const responseJson = await result.json<PlusSignupEndpointResponse>();
        handleShowWelcome();
        let path;
        switch (true) {
          case responseJson.wasCredited:
            path = `${window.location.href.split("?")[0]}?enrolled=1&wasCredited=${responseJson.wasCredited}`;
            break;
          case isOpenFromAd:
            path = window.location.href;
            break;
          default:
            path = `${window.location.href.split("?")[0]}?enrolled=1`;
            break;
        }
        setOnCloseUrlUpdate(path);
      } else {
        setSubscribeError(true);
        setSubmitting(false);
      }
      return {
        isError: false
      };
    } catch (e) {
      const error = new Error(`Error sending Plus enrollment to /plus/enroll/`, {
        cause: toError(e)
      });
      setSubscribeError(true);
      setSubmitting(false);
      await logException(error);
      return {
        error,
        isError: true
      };
    }
  };
  const handleShowWelcome = () => {
    if (!modalRef.current) return;
    const el = modalRef.current;
    setWelcomeMessageIsVisible(true);
    el.scrollTop = 0;
    setTimeout(() => {
      el.style.overflow = "hidden";
      el.style.background = "#022E44";
    }, 500);
  };
  const submitForm = async (): Promise<Throwable<void>> => {
    if (shippingId === 0) {
      setSubscribeCustomErrorMessage("Please select a shipping address");
      const error = new Error("A shipping address must be selected");
      return {
        error,
        isError: true
      };
    }
    setSubmitting(true);
    setSubscribeCustomErrorMessage("");
    if (!paymentSelectionRef) {
      return {
        isError: false
      };
    }
    const tokenizeResult = await paymentSelectionRef.current?.tokenize();
    if (tokenizeResult?.suggestVerifyInformation) {
      setCardVerificationError(true);
      setSubmitting(false);
    } else if (!tokenizeResult?.isTokenized) {
      setCardSaveError(true);
      setSubmitting(false);
    } else if (addressPlusSignup) {
      return submitAddSubscriptionRequest(shippingId, tokenizeResult.tokenId);
    } else {
      return submitNewSubscriptionEnrollmentRequest(tokenizeResult.tokenId);
    }
    return {
      isError: false
    };
  };
  useEffect(() => {
    const dialog = document.querySelector("[aria-label='Plus Subscription Modal']");
    if (plusSubscriptionModalOpen && dialog?.getAttribute("open") === null) {
      setPlusSubscriptionModalOpen(ModalTypes.PLUS);
    }
  }, [setPlusSubscriptionModalOpen, plusSubscriptionModalOpen]);
  useEffect(() => {
    if (shippingId === 0) {
      if (defaultShippingAddressId != 0) {
        setShippingId(defaultShippingAddressId);
      }
      if ((shippingAddresses?.length ?? 0) > 0) {
        setShippingId(shippingAddresses.find(() => true)?.id ?? 0);
      }
    }
  }, [defaultShippingAddressId, shippingAddresses, shippingId]);
  useEffect(() => {
    if (enrollment?.promotionName) {
      setPromotionName(enrollment.promotionName);
    }
  }, [enrollment]);
  useEffect(() => {
    if (!plusSubscriptionModalOpen) return;
    const modalContainer = modalRef.current;
    if (modalContainer) {
      modalContainer.setAttribute("tabindex", "-1");
      modalContainer.focus();
      modalContainer.removeAttribute("tabindex");
    }
  }, [plusSubscriptionModalOpen]);
  return <Modal ref={modalRef} theme="wss" open={plusSubscriptionModalOpen} onOpenChange={onOpenChange} aria-label="Plus Subscription Modal" data-testid="plus-subscription-modal" id={plusModalId} className={`w-full max-w-[579px] xxm:rounded-2xl lm:max-w-[959px] lz:max-w-5xl overflow-auto scrollbar-width-none `} data-sentry-element="Modal" data-sentry-component="PlusSubscriptionModal" data-sentry-source-file="PlusSubscriptionModal.tsx">
      <div className="m-0 min-h-screen">
        <div className="relative w-full lm:h-full overflow-hidden flex flex-col-reverse lm:flex-row">
          {/* form - left side*/}
          <div className={cls("relative transition-[width,height] duration-[300ms] ease-in-out overflow-hidden", welcomeMessageIsVisible ? "lm:w-0" // desktop: collapse width, mobile: collapse height
        : "h-auto lm:w-1/2")}>
            <div className="px-6 pb-6 xs:px-10 lm:px-12 md:pb-10 lm:w-[393px]">
              <div className="flex font-bold py-6 lm:pt-10 border-b border-gray-200 text-gray-900 text-2xl lm:text-3xl lm:text-center">
                <span>Get</span>
                <span className="text-green-500 whitespace-pre"> FREE </span>
                <span>shipping & more!</span>
              </div>
              {plusSubscriptionModalOpen && <ShippingDropdown isDisabled={false} setShippingId={setShippingId} shippingAddresses={shippingAddresses} selectedAddressId={shippingId} isFreeview={isFreeview} />}
              <WebPlusPaymentDetails enrollmentOptions={enrollmentOptions?.map(option => option)} formattedPlusPrice={formattedPlusPrice} setPromotionName={setPromotionName} promotionName={promotionName} isFreeview={isFreeview} subscriptionChargeDate={subscriptionChargeDate} data-sentry-element="WebPlusPaymentDetails" data-sentry-source-file="PlusSubscriptionModal.tsx" />
              <div className="px-0 pt-6 pb-1 border-0 border-b border-solid border-gray-200">
                <fieldset className="p-0 border-0 lm:flex-col">
                  <legend className="w-full p-0 text-base font-bold text-gray-700">
                    Payment Method
                  </legend>
                  {plusSubscriptionModalOpen && <PaymentSelection ref={paymentSelectionRef} setSavedCardId={setSavedCardId} defaultPaymentMethod={PaymentMethod.SavedCard} defaultTokenId={savedCardId} defaultSavedCards={savedCardData.map(element => ({
                  id: element.num,
                  type: element.creditCardType,
                  mask: element.lastFour,
                  name: element.name,
                  isExpired: element.expirationDate > todaysDate,
                  isRewardsCard: element.isRewardsCard
                }))} />}
                </fieldset>
                <Errors cardVerificationError={cardVerificationError} cardSaveError={cardSaveError} subscribeError={subscribeError} subscribeCustomErrorMessage={subscribeCustomErrorMessage} data-sentry-element="Errors" data-sentry-source-file="PlusSubscriptionModal.tsx" />
              </div>
              <Footer addressPlusSignup={addressPlusSignup} setMarketingOptIn={setMarketingOptIn} marketingOptIn={marketingOptIn} submitting={submitting} submitForm={submitForm} data-sentry-element="Footer" data-sentry-source-file="PlusSubscriptionModal.tsx" />
            </div>
          </div>

          {/* Testimony/WelcomeMessage - right side */}
          <div className={cls("transition-[width,height] duration-[300ms] ease-in-out overflow-hidden", welcomeMessageIsVisible ? "absolute inset-0 lm:relative h-full lm:w-full" // desktop: expand width, mobile: expand height
        : "h-[10%] lm:w-1/2 lm:h-full")}>
            <Header onClose={handleModalClose} welcomeMessageIsVisible={welcomeMessageIsVisible} modalID={plusModalId} data-sentry-element="Header" data-sentry-source-file="PlusSubscriptionModal.tsx" />
          </div>
        </div>
      </div>
    </Modal>;
};
export default PlusSubscriptionModal;